import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import { getRestrictedBrand, useBrand } from "./BrandContext";

export const PROSPECT_STATUSES = ["À appeler", "Appelé sans réponse", "Dossier à créer", "Rejeté"] as const;
export type ProspectStatus = typeof PROSPECT_STATUSES[number];

export type Prospect = {
  id: string; received_at: string; full_name: string; email: string | null; phone: string | null;
  center: string | null; client_type: string | null; participants_range: string | null;
  status: ProspectStatus; call_note: string; call_note_version: number; case_id: string | null;
  prospect_reads: { read_at: string }[];
};
const SELECT = "id,received_at,full_name,email,phone,center,client_type,participants_range,status,call_note,call_note_version,prospect_reads(read_at),cases(id)";
type ProspectsState = {
  prospects: Prospect[]; unreadCount: number; loading: boolean; error: string | null;
  lastSyncAt: string | null; refresh: () => void; markRead: (id: string) => Promise<void>;
  setStatus: (id: string, status: ProspectStatus, previous: ProspectStatus) => Promise<void>;
};
const Context = createContext<ProspectsState | undefined>(undefined);

export function ProspectsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { brand } = useBrand();
  const enabled = !!user && brand === "smile" && !getRestrictedBrand(user.email);
  const owner = enabled ? user!.id : null;
  const [result, setResult] = useState<{ owner: string | null; prospects: Prospect[]; lastSyncAt: string | null }>({ owner: null, prospects: [], lastSyncAt: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(n => n + 1), []);

  useEffect(() => {
    if (!owner) { setResult({ owner: null, prospects: [], lastSyncAt: null }); setError(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    async function fetchProspects() {
      try {
        // PostgREST limite chaque réponse : parcourir les pages pour conserver un compteur exact.
        const prospects: Prospect[] = [];
        for (let offset = 0; ; offset += 1000) {
          const { data, error: queryError } = await supabase.from("prospects").select(SELECT)
            .order("received_at", { ascending: false }).order("id", { ascending: false }).range(offset, offset + 999);
          if (cancelled) return;
          if (queryError) throw queryError;
          for (const row of data) {
            const linked = row.cases as { id: string } | { id: string }[] | null;
            prospects.push({ ...row, case_id: (Array.isArray(linked) ? linked[0] : linked)?.id ?? null });
          }
          if (data.length < 1000) break;
        }
        const { data: sync, error: syncError } = await supabase.from("prospect_sync_state").select("last_success_at").eq("source", "smile-meta-sheet").maybeSingle();
        if (syncError) throw syncError;
        if (!cancelled) { setResult({ owner, prospects, lastSyncAt: sync?.last_success_at ?? null }); setError(null); }
      } catch { if (!cancelled) setError("Impossible de charger les prospects. Réessayez dans un instant."); }
      finally { if (!cancelled) setLoading(false); }
    }
    void fetchProspects();
    return () => { cancelled = true; };
  }, [owner, revision]);

  useEffect(() => {
    if (!owner) return;
    let debounce: ReturnType<typeof setTimeout>;
    const schedule = () => { clearTimeout(debounce); debounce = setTimeout(refresh, 250); };
    const channel = supabase.channel(`prospects:${owner}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "prospects" }, schedule)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "cases" }, schedule)
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "cases" }, schedule)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "prospect_reads", filter: `profile_id=eq.${owner}` }, schedule)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "prospect_sync_state" }, schedule)
      .subscribe();
    const refreshVisible = () => { if (document.visibilityState === "visible") schedule(); };
    const interval = setInterval(refreshVisible, 60_000);
    window.addEventListener("focus", refreshVisible);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      clearTimeout(debounce); clearInterval(interval);
      window.removeEventListener("focus", refreshVisible);
      document.removeEventListener("visibilitychange", refreshVisible);
      void supabase.removeChannel(channel);
    };
  }, [owner, refresh]);

  const markRead = useCallback(async (id: string) => {
    if (!owner) throw new Error("Accès refusé");
    const { error: readError } = await supabase.rpc("mark_prospect_read", { p_prospect_id: id });
    if (readError) throw new Error("La lecture n’a pas pu être enregistrée. Réessayez.");
    setResult(current => current.owner !== owner ? current : {
      ...current, prospects: current.prospects.map(p => p.id === id ? { ...p, prospect_reads: [{ read_at: new Date().toISOString() }] } : p),
    });
    refresh();
  }, [owner, refresh]);

  const setStatus = useCallback(async (id: string, status: ProspectStatus, previous: ProspectStatus) => {
    if (!owner) throw new Error("Accès refusé");
    const { data, error: saveError } = await supabase.from("prospects")
      .update({ status }).eq("id", id).eq("status", previous).select("id").maybeSingle();
    if (saveError) throw new Error("Le statut n’a pas pu être enregistré. Réessayez.");
    if (!data) {
      refresh();
      throw new Error("Ce prospect a été modifié par une autre personne. Vérifiez son statut avant de réessayer.");
    }
    setResult(current => current.owner !== owner ? current : {
      ...current, prospects: current.prospects.map(p => p.id === id ? { ...p, status } : p),
    });
    refresh();
  }, [owner, refresh]);

  const prospects = result.owner === owner && owner ? result.prospects : [];
  const unreadCount = prospects.filter(p => p.prospect_reads.length === 0).length;
  const value = useMemo(() => ({ prospects, unreadCount, loading, error, lastSyncAt: result.owner === owner ? result.lastSyncAt : null, refresh, markRead, setStatus }),
    [prospects, unreadCount, loading, error, result, owner, refresh, markRead, setStatus]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useProspects() {
  const value = useContext(Context);
  if (!value) throw new Error("ProspectsProvider manquant");
  return value;
}

export function ProspectsNavLabel() {
  const { unreadCount } = useProspects();
  return <span className="inline-flex items-center gap-2">Prospects{unreadCount > 0 && <>
    <span className="h-2 w-2 rounded-full bg-red-500" aria-hidden="true" />
    <span className="sr-only">{unreadCount} non lu{unreadCount > 1 ? "s" : ""}</span>
  </>}</span>;
}
